/**
 * When a lent NPC's loan ends (take-back, scene finish, decline), its entry drops out of
 * the account's `mine` list. A tab that was playing it must not keep the NPC as its
 * speaker: it falls back to the account's own remaining character, a PC before an NPC
 * (#4418). The list is refetched on focus and on an interval; the server pushes nothing
 * for loan changes.
 */
import { useEffect, useRef } from 'react';

import { isStoryNpc } from '@/roster/persona';
import type { MyRosterEntry } from '@/roster/types';
import { useSelectCharacterMutation } from '@/roster/queries';
import { writeTabIdentity } from '@/store/browsingIdentity';
import { hydrateActiveCharacter, setActiveSession, setBrowsingIdentity } from '@/store/gameSlice';
import { useAppDispatch } from '@/store/hooks';

/** A PC if the account has one, else any remaining character. */
export function fallbackEntry(characters: MyRosterEntry[]): MyRosterEntry | null {
  return characters.find((c) => !isStoryNpc(c)) ?? characters[0] ?? null;
}

export function useLoanEndFallback(
  characters: MyRosterEntry[],
  active: string | null,
  hasSession: (name: string) => boolean,
  /** Closes the ended loan's socket and forgets its session tab. */
  dropSession: (name: string) => void
): void {
  const dispatch = useAppDispatch();
  const { mutate: selectCharacter } = useSelectCharacterMutation();
  // The last listed character this tab was on, by roster entry id: a rename (a crowd
  // voice described again) changes the name but not the entry.
  const last = useRef<{ id: number; name: string } | null>(null);

  useEffect(() => {
    // Only a character seen in the list counts: a name that never was there did not leave it.
    const current = active ? characters.find((c) => c.name === active) : undefined;
    if (current) last.current = { id: current.id, name: current.name };
    const previous = last.current;
    if (!previous || characters.length === 0) return;
    const same = characters.find((c) => c.id === previous.id);
    if (same) {
      if (same.name !== previous.name) {
        // Renamed while played: follow the new name, nothing ended.
        last.current = { id: same.id, name: same.name };
        if (active === previous.name) {
          dispatch(hydrateActiveCharacter({ name: same.name, entryId: same.id }));
        }
      }
      return;
    }
    dropSession(previous.name);
    const next = fallbackEntry(characters);
    if (!next) return;
    last.current = { id: next.id, name: next.name };
    writeTabIdentity(next.id);
    dispatch(setBrowsingIdentity(next.id));
    if (hasSession(next.name)) {
      dispatch(setActiveSession(next.name));
    } else {
      dispatch(hydrateActiveCharacter({ name: next.name, entryId: next.id }));
    }
    selectCharacter(next.id);
  }, [characters, active, dispatch, hasSession, dropSession, selectCharacter]);
}
